'use client';

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImagesModalSectionProps, MediaModalSectionName } from '@/features/media-modal/components/common/commonTypes';
import * as MediaAssetAPI from '@/api/media-asset';
import MediaGrid from '@/components/media-asset/MediaGrid';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import { useCurrentApp } from '@/stores/userDataStore';
import MediaAssetsLoader from '@/features/media-assets/components/MediaAssetsLoader';
import { ICardMediaAsset, MediaAssetType, MediaAssetStatus, StandaloneMediaAsset } from '@/types/media';
import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import { getOutputBucketUrl } from '@/utils/public-urls';
import { Box, Text, BlockStack, Card, InlineStack, Button } from '@shopify/polaris';
import { NoContentButton } from './sections-content/NoContentButton';
import { useQueryClient } from '@tanstack/react-query';
import ProductFilter from '@/features/products/filters/ProductFilter';
import { Product } from '@/types/product';

const BunuImagesModalSection = ({
  selectedMedia,
  setSelectedMedia,
  setSelectedSection,
  multiSelection = true,
}: ImagesModalSectionProps) => {
  const { appKey } = useCurrentApp();
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['image-assets', appKey] });
  }, [appKey, queryClient]);

  const handleProductChange = useCallback((product: Product | null) => {
    setSelectedProduct(product);
  }, []);

  const handleSelect = useCallback(
    (media: StandaloneMediaAsset, selected: boolean) => {
      const formattedMedia = {
        ...media,
        provider: MediaAssetProviders.aiGenerated,
        settings: undefined,
        src: `${getOutputBucketUrl()}/${media.result}`,
      };

      setSelectedMedia((prev) => {
        if (!multiSelection) {
          return selected ? [formattedMedia] : [];
        }

        return selected ? [...prev, formattedMedia] : prev.filter((item) => item.id !== media.id);
      });
    },
    [setSelectedMedia, multiSelection]
  );

  const loaderParams = {
    queryKey: ['image-assets', appKey],
    loaderFn: MediaAssetAPI.getAiImages,
  };

  const handleEmptyStateAction = useCallback(() => {
    setSelectedSection(MediaModalSectionName.aiImage);
  }, [setSelectedSection]);

  const renderItem = useCallback(
    (item) => {
      return (
        <MediaAssetCard
          key={item.id}
          media={item as ICardMediaAsset}
          size="small"
          hideMetadata
          hideUseAction
          selected={selectedMedia.some((media) => media.id === item.id)}
          onSelect={(media, selected) => handleSelect(media as StandaloneMediaAsset, selected)}
        />
      );
    },
    [handleSelect, selectedMedia]
  );

  const renderMediaAssets = useCallback(
    (mediaAssets: StandaloneMediaAsset[]) => {
      let filteredAssets = mediaAssets.filter(
        (asset) => asset.type === MediaAssetType.image && asset.status === MediaAssetStatus.DONE
      );

      if (selectedProduct) {
        filteredAssets = filteredAssets.filter(
          (asset) => asset.productId === selectedProduct.productId || asset.productId === selectedProduct.id
        );
      }

      if (!filteredAssets || filteredAssets.length === 0) {
        return (
          <div className="flex h-60 items-center justify-center">
            <Box background="bg-surface-secondary" padding="600" borderRadius="300">
              <BlockStack gap="400" align="center">
                <Text as="h2" variant="headingMd" alignment="center">
                  No images found
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                  {selectedProduct
                    ? 'No AI images found for this product. Try selecting a different product.'
                    : 'No AI images found. Create some AI images to see them here.'}
                </Text>
              </BlockStack>
            </Box>
          </div>
        );
      }

      return <MediaGrid items={filteredAssets} size="medium" renderItem={renderItem} />;
    },
    [renderItem, handleEmptyStateAction, selectedProduct]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex-none">
        <BlockStack gap="400">
          <Box padding="200">
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Bunu images
              </Text>

              <Card>
                <Box padding="200">
                  <InlineStack gap="200" blockAlign="start">
                    <ProductFilter selectedProduct={selectedProduct} onChange={handleProductChange} />
                  </InlineStack>
                </Box>
              </Card>
            </BlockStack>
          </Box>
        </BlockStack>
      </div>
      <Box padding="200">
        <MediaAssetsLoader hideFailed {...loaderParams} renderMediaAssets={renderMediaAssets} size="medium" />
      </Box>
    </div>
  );
};

export default memo(BunuImagesModalSection);
